import { Controller } from '@hotwired/stimulus';
import {
  submitFeedback,
  continueToNextSection,
  currentSection,
  updateDocumentUserState,
  showAiErrorModal,
  handleAiAssistantFallback,
  AI_VPE_CHAT_MINIMUM_MESSAGE_COUNT,
  registerErrorInRollbar,
  updateProgressFromResponse,
  getCsrfToken,
  getDusFromDom,
  vpeChatErrorBlocksFeedback,
  removeSpeechToTextToolbarFromContainer,
  seemsOffline,
} from '../utils/case_experience_in_rails';
import AiFeedbackModal from '../utils/ai_feedback_modal';
import { normalizeNodeUid } from '../utils/case_experience/normalize_node_uid';

const MAX_RETRIES = 2;
/** Safety net if assistant reply never arrives while _awaitingAssistantResponse is true */
const RESPONSE_SAFETY_TIMEOUT_MS = 30000;

function vpeAiFallbackCardsAppliedInDus(dus) {
  return dus?.root?.aiFallbackCards?.value === true;
}

function dispatchVpeFeedbackCardMayGenerate(documentId, chatNodeUid) {
  document.dispatchEvent(
    new CustomEvent('vpe-feedback-card-may-generate', {
      bubbles: true,
      detail: {
        documentId,
        chatUid: normalizeNodeUid(chatNodeUid),
      },
    }),
  );
}

/**
 * Rails/Turbo VPE chat: ties form submit, thread messages DOM, DUS sync, and send/feedback UI.
 *
 * UI state axis (for debugging): _awaitingAssistantResponse drives input disabled + placeholder +
 * send button; it is cleared by enableInputForNextTurn (happy path), restoreFormAfterError (failure),
 * or disconnect. Turbo may replace the message form after submit — inputTargetConnected +
 * applyAwaitingAssistantInputUi re-sync the DOM input to that flag.
 */
export default class extends Controller {
  static targets = [
    'input',
    'messagesContainer',
    'sendButton',
    'feedbackButton',
    'feedbackModal',
    'errorModal',
  ];

  static values = {
    documentId: Number,
    userId: Number,
    nodeUid: String,
    assistantId: String,
    threadId: String,
    messages: Array,
    feedbackSubmitted: Boolean,
  };

  /** Turbo fetch failures + assistant-side failures (e.g. OpenAI error message) before fatal modal; aligns with React errorCount >= 2 */
  errorCount = 0;
  /** True while the VPE chat error overlay is shown (avoids stacking modals / duplicate work) */
  errorModalOpen = false;
  /** Dedupe counting when finishTurnIfReady runs multiple times for same assistant-error turn (messagesValue length key) */
  _lastCountedAssistantErrorKey = null;
  /** Debounce timer for sync after messages DOM mutations */
  _syncDebounce = null;
  /** Fires restoreFormAfterError + error modal if still awaiting after RESPONSE_SAFETY_TIMEOUT_MS */
  _responseSafetyTimeout = null;
  /**
   * True from turbo:submit-start until assistant message is done or we error out.
   * false: user can type; true: input should be disabled + “AI is responding” placeholder.
   */
  _awaitingAssistantResponse = false;
  /** One-shot refetch after connect if thread looks stuck (last msg user-only or empty assistant) */
  _connectRecoveryRefetchScheduled = false;
  /** Delayed handler id for scheduleConnectStuckTurnRecoveryIfNeeded */
  _connectRecoveryTimeoutId = null;

  /** Wire observers, sync thread from server if we already have a thread id, recover stuck turns */
  connect() {
    this._updateSendButtonState = this.updateSendButtonState.bind(this);
    this._onMessagesDomMutated = this.debouncedSyncAfterMessagesDomChange.bind(this);
    this._onAfterTurboStreamRender = this.afterTurboStreamRender.bind(this);
    document.addEventListener('turbo:after-stream-render', this._onAfterTurboStreamRender);
    if (this.hasMessagesContainerTarget) {
      this._mutationObserver = new MutationObserver(this._onMessagesDomMutated);
      this._mutationObserver.observe(this.messagesContainerTarget, { childList: true, subtree: true });
    }

    if (this.hasInputTarget) {
      this.inputTarget.addEventListener('input', this._updateSendButtonState);
    }
    this.updateSendButtonState();

    if (this.feedbackSubmittedValue) {
      if (this.hasInputTarget) this.inputTarget.disabled = true;
      if (this.hasSendButtonTarget) this.sendButtonTarget.disabled = true;
      removeSpeechToTextToolbarFromContainer(this.element);
    }

    this.messagesValueChanged();

    if (this.parseThreadIdFromForm() || (this.hasThreadIdValue && this.threadIdValue)) {
      void this.syncMessagesToDus().then((ok) => {
        if (ok) {
          this.finishTurnIfReady();
          this.scheduleConnectStuckTurnRecoveryIfNeeded();
        }
      });
    }
  }

  disconnect() {
    this._mutationObserver?.disconnect();
    this._mutationObserver = null;
    // Tear down: stop treating any in-flight turn as active (input may already be gone)
    this._awaitingAssistantResponse = false;
    clearTimeout(this._syncDebounce);
    clearTimeout(this._responseSafetyTimeout);
    clearTimeout(this._connectRecoveryTimeoutId);
    this._connectRecoveryTimeoutId = null;
    this._connectRecoveryRefetchScheduled = false;
    this._lastCountedAssistantErrorKey = null;
    if (this._updateSendButtonState && this.hasInputTarget) {
      this.inputTarget.removeEventListener('input', this._updateSendButtonState);
    }
    if (this._onAfterTurboStreamRender) {
      document.removeEventListener('turbo:after-stream-render', this._onAfterTurboStreamRender);
    }
  }

  // After Action Cable / turbo streams append messages, pull JSON thread into messagesValue + DUS
  afterTurboStreamRender = () => {
    if (this.feedbackSubmittedValue || !this._awaitingAssistantResponse) return;
    if (!this.getResolvedThreadId()) return;
    void this.syncMessagesToDus().then((ok) => {
      if (ok) this.finishTurnIfReady();
    });
  };

  get isInputEmpty() {
    return !this.hasInputTarget || this.inputTarget.value.trim() === '';
  }

  updateSendButtonState = () => {
    if (!this.hasSendButtonTarget) return;
    this.sendButtonTarget.disabled =
      this.isInputEmpty ||
      this.feedbackSubmittedValue ||
      this.inputTarget?.disabled === true ||
      this._awaitingAssistantResponse;
  };

  submitOnEnter = (event) => {
    if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return;
    event.preventDefault();
    if (!this.hasInputTarget || this.inputTarget.disabled) return;
    if (!this.hasSendButtonTarget || this.sendButtonTarget.disabled) return;
    this.inputTarget.closest('form')?.requestSubmit();
  };

  /**
   * Sync visible input to _awaitingAssistantResponse. Required because create_vpe turbo_stream
   * replaces the form: new input loses disabled/placeholder unless we re-apply here.
   */
  applyAwaitingAssistantInputUi() {
    if (!this.hasInputTarget || this.feedbackSubmittedValue) return;
    if (this._awaitingAssistantResponse) {
      this.inputTarget.disabled = true;
      this.inputTarget.placeholder = 'AI is responding...';
    } else {
      this.inputTarget.disabled = false;
      this.inputTarget.placeholder = 'Type a message';
    }
  }

  // Stimulus: new input target (e.g. after turbo replaces vpe_thread_message_form)
  inputTargetConnected() {
    this.applyAwaitingAssistantInputUi();
    this.updateSendButtonState();
  }

  sendButtonTargetConnected() {
    this.updateSendButtonState();
  }

  messagesValueChanged = () => {
    if (this.hasFeedbackButtonTarget) {
      this.feedbackButtonTarget.disabled =
        !this.shouldEnableFeedbackButton() || this.feedbackSubmittedValue;
    }
  };

  getUserMessageCount = () => {
    return Array.isArray(this.messagesValue)
      ? this.messagesValue.filter((msg) => String(msg.role) === 'user').length
      : 0;
  };

  shouldEnableFeedbackButton = () =>
    this.getUserMessageCount() >= AI_VPE_CHAT_MINIMUM_MESSAGE_COUNT;

  parseThreadIdFromForm() {
    const form = this.element.querySelector('form[action*="/ai_assistant/threads/"]');
    if (!form?.action) return null;
    const m = form.action.match(/\/ai_assistant\/threads\/(\d+)\/messages/);
    return m ? m[1] : null;
  }

  parseThreadIdFromMessagesMount() {
    if (!this.hasMessagesContainerTarget) return null;
    const el = this.messagesContainerTarget.querySelector('[id^="thread_messages_"]');
    const m = el?.id?.match(/^thread_messages_(\d+)$/);
    return m ? m[1] : null;
  }

  getResolvedThreadId() {
    const fromForm = this.parseThreadIdFromForm();
    if (fromForm) return fromForm;
    const fromMount = this.parseThreadIdFromMessagesMount();
    if (fromMount) return fromMount;
    return this.hasThreadIdValue && this.threadIdValue ? String(this.threadIdValue) : null;
  }

  debouncedSyncAfterMessagesDomChange = () => {
    const threadId = this.getResolvedThreadId();
    const mount = threadId ? document.getElementById(`thread_messages_${threadId}`) : null;
    if (!threadId) return;
    if (!mount || !this.element.contains(mount)) return;
    clearTimeout(this._syncDebounce);
    this._syncDebounce = setTimeout(() => {
      void this.syncMessagesToDus().then((ok) => {
        if (ok) this.finishTurnIfReady();
      });
    }, 150);
  };

  /** Thread still waiting on assistant (non-empty user last, or empty assistant placeholder) */
  stuckAwaitingAssistantFromMessages(messages) {
    const msgs = Array.isArray(messages) ? messages : [];
    if (msgs.length === 0) return false;
    const last = msgs[msgs.length - 1];
    if (String(last?.role) === 'user' && String(last.content || '').trim()) return true;
    if (String(last?.role) === 'assistant' && !String(last.content || '').trim()) return true;
    return false;
  }

  scheduleConnectStuckTurnRecoveryIfNeeded() {
    if (this.feedbackSubmittedValue || this._connectRecoveryRefetchScheduled) return;
    if (!this.stuckAwaitingAssistantFromMessages(this.messagesValue)) return;
    this._connectRecoveryRefetchScheduled = true;
    this._connectRecoveryTimeoutId = setTimeout(() => {
      this._connectRecoveryTimeoutId = null;
      void this.syncMessagesToDus().then((ok) => {
        if (ok) this.finishTurnIfReady();
      });
    }, 2000);
  }

  /** GET thread JSON, update messagesValue + persist node slice to DUS; then messagesValueChanged / send button */
  async syncMessagesToDus() {
    const threadId = this.getResolvedThreadId();
    if (!threadId || !this.documentIdValue || !this.userIdValue || !this.nodeUidValue) return false;

    try {
      const res = await fetch(
        `/ai_assistant/messages/sync_from_thread?thread_id=${encodeURIComponent(threadId)}`,
        {
          headers: {
            Accept: 'application/json',
            'X-CSRF-Token': getCsrfToken(),
          },
        },
      );
      if (!res.ok) return false;

      let data;
      try {
        data = await res.json();
      } catch {
        return false;
      }

      this.threadIdValue = data.thread_id;
      this.messagesValue = data.messages;

      await updateDocumentUserState({
        documentId: this.documentIdValue,
        userId: this.userIdValue,
        body: {
          document_user_state: {
            state: {
              [normalizeNodeUid(this.nodeUidValue)]: {
                threadId: { value: data.thread_id, updatedAt: Date.now() },
                messages: { value: data.messages, updatedAt: Date.now() },
              },
            },
          },
        },
      });

      this.messagesValueChanged();
      this.updateSendButtonState();

      if (this._awaitingAssistantResponse && this.stuckAwaitingAssistantFromMessages(this.messagesValue)) {
        this.scheduleResponseSafetyTimeout();
      }

      return true;
    } catch {
      return false;
    }
  }

  /**
   * If messagesValue shows a completed assistant turn (or model error string), leave awaiting state:
   * re-enable input, restore placeholder, optionally focus. No-op if still mid-turn.
   */
  finishTurnIfReady() {
    if (this.feedbackSubmittedValue) return;
    const msgs = Array.isArray(this.messagesValue) ? this.messagesValue : [];
    const last = msgs[msgs.length - 1];
    const assistantDone =
      String(last?.role) === 'assistant' &&
      String(last.content || '').trim() &&
      !String(last.content || '').includes('We had trouble generating');
    const assistantError =
      String(last?.role) === 'assistant' &&
      String(last.content || '').includes('We had trouble generating');
    if (assistantError) {
      const { isConsecutiveError, errorTurnKey } = this._checkConsecutiveAssistantError(msgs);

      if (
        !vpeAiFallbackCardsAppliedInDus(getDusFromDom()) &&
        isConsecutiveError &&
        this._lastCountedAssistantErrorKey !== errorTurnKey
      ) {
        this._lastCountedAssistantErrorKey = errorTurnKey;
        this.errorCount += 2;
        this.maybeOpenFatalErrorModalIfMaxRetries();
      }
      this.enableInputForNextTurn();
    } else if (assistantDone) {
      this.enableInputForNextTurn();
    }
  }

  /**
   * Check if the assistant error is consecutive to display the error modal
   */
  _checkConsecutiveAssistantError(msgs) {
    let prevAssistant = null;
    for (let i = msgs.length - 2; i >= 0; i -= 1) {
      if (String(msgs[i]?.role) === 'assistant') {
        prevAssistant = msgs[i];
        break;
      }
    }
    const consecutiveAssistantErrors =
      prevAssistant != null &&
      String(prevAssistant.content || '').includes('We had trouble generating');
    const errorTurnKey = consecutiveAssistantErrors ? String(msgs.length - 1) : null;
    return { isConsecutiveError: consecutiveAssistantErrors, errorTurnKey };
  }

  /** Same threshold as React AiAssistantChat (errorCount >= 2) and formFetchError */
  maybeOpenFatalErrorModalIfMaxRetries = () => {
    if (this.errorCount < MAX_RETRIES || this.errorModalOpen) return;
    if (vpeAiFallbackCardsAppliedInDus(getDusFromDom())) return;
    this.errorModalOpen = true;
    void this.recordChatErrorAndNotifyFeedback()
      .catch(() => {})
      .finally(() => {
        this.showErrorModal();
      });
  };

  // While awaiting, re-arm if sync still shows a stuck thread (safety timeout -> modal + restore form)
  scheduleResponseSafetyTimeout() {
    if (this.feedbackSubmittedValue) return;
    clearTimeout(this._responseSafetyTimeout);
    this._responseSafetyTimeout = setTimeout(() => {
      if (this.feedbackSubmittedValue || !this._awaitingAssistantResponse) return;
      this.restoreFormAfterError();
      if (!this.errorModalOpen && !vpeAiFallbackCardsAppliedInDus(getDusFromDom())) {
        this.errorModalOpen = true;
        void this.recordChatErrorAndNotifyFeedback()
          .catch(() => {})
          .finally(() => {
            this.showErrorModal();
          });
      }
    }, RESPONSE_SAFETY_TIMEOUT_MS);
  }

  /** Happy path: assistant finished -> _awaitingAssistantResponse false, input live, focus if user had submitted */
  enableInputForNextTurn() {
    clearTimeout(this._responseSafetyTimeout);
    this._responseSafetyTimeout = null;
    if (this.feedbackSubmittedValue) return;
    const shouldFocus = this._awaitingAssistantResponse;
    this._awaitingAssistantResponse = false;
    if (this.hasInputTarget) {
      this.inputTarget.disabled = false;
      this.inputTarget.placeholder = 'Type a message';
    }
    this.updateSendButtonState();
    if (this.hasInputTarget && shouldFocus) {
      requestAnimationFrame(() => {
        this.inputTarget.focus();
      });
    }
  }

  // User sent a message (Enter or send): enter awaiting state + UI lock until reply or error
  formSubmitStart = () => {
    if (this.feedbackSubmittedValue) return;
    this._awaitingAssistantResponse = true;
    this.scheduleResponseSafetyTimeout();
    this.applyAwaitingAssistantInputUi();
    if (this.hasSendButtonTarget) this.sendButtonTarget.disabled = true;
  };

  // Turbo submit finished: on success sync thread; finishTurnIfReady runs when assistant msg exists
  formSubmitEnd = (e) => {
    if (!e.detail.success) {
      this.restoreFormAfterError();
      return;
    }
    void this.syncMessagesToDus().then((ok) => {
      if (ok) this.finishTurnIfReady();
    });
  };

  // Network/submit failure: leave awaiting, re-enable form; may open error modal after MAX_RETRIES
  formFetchError = () => {
    clearTimeout(this._responseSafetyTimeout);
    this._responseSafetyTimeout = null;
    this.restoreFormAfterError();
    this.errorCount += 1;
    if (seemsOffline()) this.errorCount = MAX_RETRIES;
    this.maybeOpenFatalErrorModalIfMaxRetries();
  };

  /** Recover typing UI without modal (safety timeout first step, or submit failure before max retries) */
  restoreFormAfterError() {
    clearTimeout(this._responseSafetyTimeout);
    this._responseSafetyTimeout = null;
    if (this.feedbackSubmittedValue) return;
    this._awaitingAssistantResponse = false;
    if (this.hasInputTarget) {
      this.inputTarget.disabled = false;
      this.inputTarget.placeholder = 'Type a message';
    }
    this.updateSendButtonState();
  }

  recordChatErrorAndNotifyFeedback = async () => {
    await updateDocumentUserState({
      documentId: this.documentIdValue,
      userId: this.userIdValue,
      body: {
        document_user_state: {
          state: {
            root: {
              error: { value: 'aiAssistantChatError', updatedAt: new Date().getTime() },
            },
          },
        },
      },
    });
    document.dispatchEvent(new CustomEvent('vpe-chat-error', { bubbles: true }));
  };

  // Fatal chat error overlay; CONTINUE runs enableAiFallbackMode (DUS + collapse AI sections)
  showErrorModal = () => {
    showAiErrorModal({
      onContinue: () => this.enableAiFallbackMode(),
      buttonId: 'ai-assistant-error-modal-continue-button',
    });
  };

  // After fallback navigation or dismiss cleanup
  hideErrorModal = () => {
    document.querySelectorAll('.ai-error-modal').forEach((el) => el.remove());
    document.body.style.overflow = '';
    this.errorModalOpen = false;
    this.errorCount = 0;
    this._lastCountedAssistantErrorKey = null;
  };

  async enableAiFallbackMode() {
    const body = {
      document_user_state: {
        state: {
          root: {
            error: { value: 'aiAssistantChatError', updatedAt: new Date().getTime() },
            aiFallbackCards: { value: true, updatedAt: new Date().getTime() },
          },
        },
      },
    };

    const response = await updateDocumentUserState({
      documentId: this.documentIdValue,
      userId: this.userIdValue,
      body,
    });

    if (response) {
      await handleAiAssistantFallback({
        currentSectionElement: currentSection(this.element),
        documentId: this.documentIdValue,
        userId: this.userIdValue,
        application: this.application,
        onAfterFallback: () => this.hideErrorModal(),
      });
    }
  }

  // Post-survey flow: disables chat, may signal feedback card to generate
  openFeedbackModal = (event) => {
    event?.preventDefault?.();
    if (!this.hasFeedbackModalTarget) return;
    if (this.feedbackSubmittedValue) return;
    const threadId = this.getResolvedThreadId();
    if (!threadId) {
      registerErrorInRollbar('VPE feedback opened without thread id', { nodeUid: this.nodeUidValue });
      return;
    }
    this.element.dispatchEvent(new CustomEvent('vpe-chat-interview-ending'));
    if (!this._aiFeedbackModal) {
      this._aiFeedbackModal = new AiFeedbackModal({
        experienceQuestion: 'My reaction to this virtual patient chatbot is best expressed as:',
        responseOptions: [
          'All good, patient was realistic',
          'Patient was too easy',
          'Patient was too hard',
          'Slow, unresponsive, or glitchy',
          'Other, please specify below',
        ],
        onCancel: () => this._aiFeedbackModal.close(),
        onContinue: (rating, issues, comment) => {
          this._aiFeedbackModal.close();
          if (this.feedbackSubmittedValue) return;
          const tid = this.getResolvedThreadId();
          if (!tid) return;
          submitFeedback(tid, rating, issues, comment);
          this.feedbackSubmittedValue = true;
          if (this.hasFeedbackButtonTarget) {
            this.feedbackButtonTarget.disabled = true;
          }
          if (this.hasInputTarget) {
            this.inputTarget.disabled = true;
          }
          if (this.hasSendButtonTarget) {
            this.sendButtonTarget.disabled = true;
          }
          removeSpeechToTextToolbarFromContainer(this.element);
          if (!vpeChatErrorBlocksFeedback(getDusFromDom())) {
            dispatchVpeFeedbackCardMayGenerate(this.documentIdValue, this.nodeUidValue);
          }
          this.continueToNextSection();
        },
        continueButtonTitle: 'Proceed to my feedback',
        feedbackModalTarget: this.feedbackModalTarget,
        application: this.application,
        documentIdValue: this.documentIdValue,
        userIdValue: this.userIdValue,
        threadIdValue: threadId,
      });
    }
    this._aiFeedbackModal.open();
  };

  /** Card continue (separate from feedback modal’s section navigation) */
  continueToNextSection = async () => {
    const response = await continueToNextSection({
      element: this.element,
      documentId: this.documentIdValue,
      userId: this.userIdValue,
      application: this.application,
      createContinueButtonIfNeeded: false,
    });

    if (response) {
      updateProgressFromResponse(response);
    }
  };
}
